import '@hotwired/turbo'
import { encodeMethodIntoRequestBody } from '@hotwired/turbo-rails/app/javascript/turbo/fetch_requests'

import { createApp, reactive } from 'vue'
import TemplateBuilder from './template_builder/builder'
import ImportList from './template_builder/import_list'

import ToggleVisible from './elements/toggle_visible'
import ToggleCookies from './elements/toggle_cookies'
import DisableHidden from './elements/disable_hidden'
import TurboModal from './elements/turbo_modal'
import FileDropzone from './elements/file_dropzone'
import ImportProgress from './elements/import_progress'
import ImportFrameTarget from './elements/import_frame_target'
import MenuActive from './elements/menu_active'
import ClipboardCopy from './elements/clipboard_copy'
import DynamicList from './elements/dynamic_list'
import DownloadButton from './elements/download_button'
import SetOriginUrl from './elements/set_origin_url'
import SetTimezone from './elements/set_timezone'
import AutoresizeTextarea from './elements/autoresize_textarea'
import SubmittersAutocomplete from './elements/submitter_autocomplete'
import FolderAutocomplete from './elements/folder_autocomplete'
import SignatureForm from './elements/signature_form'
import SubmitForm from './elements/submit_form'
import PromptPassword from './elements/prompt_password'
import EmailsTextarea from './elements/emails_textarea'
import ToggleSubmit from './elements/toggle_submit'
import ToggleOnSubmit from './elements/toggle_on_submit'
import CheckOnClick from './elements/check_on_click'
import PasswordInput from './elements/password_input'
import SearchInput from './elements/search_input'
import ToggleAttribute from './elements/toggle_attribute'
import LinkedInput from './elements/linked_input'
import CheckboxGroup from './elements/checkbox_group'
import MaskedInput from './elements/masked_input'
import SetDateButton from './elements/set_date_button'
import IndeterminateCheckbox from './elements/indeterminate_checkbox'
import AppTour from './elements/app_tour'
import AppTourStart from './elements/app_tour_start'
import DashboardDropzone from './elements/dashboard_dropzone'
import RequiredCheckboxGroup from './elements/required_checkbox_group'
import PageContainer from './elements/page_container'
import EmailEditor from './elements/email_editor'
import MountOnClick from './elements/mount_on_click'
import RemoveOnEvent from './elements/remove_on_event'
import ScrollTo from './elements/scroll_to'
import SetValue from './elements/set_value'
import ReviewForm from './elements/review_form'
import ShowOnValue from './elements/show_on_value'
import CustomValidation from './elements/custom_validation'
import ToggleClasses from './elements/toggle_classes'
import AutosizeField from './elements/autosize_field'
import GoogleDriveFilePicker from './elements/google_drive_file_picker'
import OpenModal from './elements/open_modal'
import BarChart from './elements/bar_chart'

import * as TurboInstantClick from './lib/turbo_instant_click'
import "./template_combiner"
import { showAlert, showConfirm } from './dialog'
TurboInstantClick.start()

// Route every native dialog through the styled one. Overriding window.alert covers all ~40
// existing callers without touching them; they all treat it as fire-and-forget, so losing
// alert()'s blocking behaviour changes nothing. confirm() genuinely blocks and cannot be
// replaced in place — Turbo takes an async method, and the few in-app callers await
// showConfirm directly.
window.alert = (message) => { showAlert(message) }
// Optional call, not optional chaining for show: if a future Turbo drops the hook this must
// degrade to the native confirm rather than throw here and take the whole bundle down.
window.Turbo?.setConfirmMethod?.((message) => showConfirm(message))

document.addEventListener('turbo:before-cache', () => {
  window.flash?.remove()
})

document.addEventListener('keyup', (e) => {
  if (e.code === 'Escape') {
    document.activeElement?.blur()
  }
})

document.addEventListener('turbo:before-fetch-request', encodeMethodIntoRequestBody)
document.addEventListener('turbo:before-fetch-request', (event) => {
  event.detail.fetchOptions.headers['X-Turbo'] = 'true'
})
document.addEventListener('turbo:submit-end', async (event) => {
  const resp = event.detail?.formSubmission?.result?.fetchResponse?.response

  if (!resp?.headers?.get('content-disposition')?.includes('attachment')) {
    return
  }

  const url = URL.createObjectURL(await resp.blob())
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', decodeURIComponent(resp.headers.get('content-disposition').split('"')[1]))

  document.body.appendChild(link)

  link.click()

  document.body.removeChild(link)

  URL.revokeObjectURL(url)
})

const safeRegisterElement = (name, element, options = {}) => !window.customElements.get(name) && window.customElements.define(name, element, options)

safeRegisterElement('toggle-visible', ToggleVisible)
safeRegisterElement('disable-hidden', DisableHidden)
safeRegisterElement('turbo-modal', TurboModal)
safeRegisterElement('file-dropzone', FileDropzone)
safeRegisterElement('import-progress', ImportProgress)
safeRegisterElement('import-frame-target', ImportFrameTarget)
safeRegisterElement('menu-active', MenuActive)
safeRegisterElement('clipboard-copy', ClipboardCopy)
safeRegisterElement('dynamic-list', DynamicList)
safeRegisterElement('download-button', DownloadButton)
safeRegisterElement('set-origin-url', SetOriginUrl)
safeRegisterElement('set-timezone', SetTimezone)
safeRegisterElement('autoresize-textarea', AutoresizeTextarea)
safeRegisterElement('submitters-autocomplete', SubmittersAutocomplete)
safeRegisterElement('folder-autocomplete', FolderAutocomplete)
safeRegisterElement('signature-form', SignatureForm)
safeRegisterElement('submit-form', SubmitForm)
safeRegisterElement('prompt-password', PromptPassword)
safeRegisterElement('emails-textarea', EmailsTextarea)
safeRegisterElement('toggle-cookies', ToggleCookies)
safeRegisterElement('toggle-submit', ToggleSubmit)
safeRegisterElement('toggle-on-submit', ToggleOnSubmit)
safeRegisterElement('password-input', PasswordInput)
safeRegisterElement('search-input', SearchInput)
safeRegisterElement('toggle-attribute', ToggleAttribute)
safeRegisterElement('linked-input', LinkedInput)
safeRegisterElement('checkbox-group', CheckboxGroup)
safeRegisterElement('masked-input', MaskedInput)
safeRegisterElement('set-date-button', SetDateButton)
safeRegisterElement('indeterminate-checkbox', IndeterminateCheckbox)
safeRegisterElement('app-tour', AppTour)
safeRegisterElement('app-tour-start', AppTourStart)
safeRegisterElement('dashboard-dropzone', DashboardDropzone)
safeRegisterElement('check-on-click', CheckOnClick)
safeRegisterElement('required-checkbox-group', RequiredCheckboxGroup)
safeRegisterElement('page-container', PageContainer)
safeRegisterElement('email-editor', EmailEditor)
safeRegisterElement('mount-on-click', MountOnClick)
safeRegisterElement('remove-on-event', RemoveOnEvent)
safeRegisterElement('scroll-to', ScrollTo)
safeRegisterElement('set-value', SetValue)
safeRegisterElement('review-form', ReviewForm)
safeRegisterElement('show-on-value', ShowOnValue)
safeRegisterElement('custom-validation', CustomValidation)
safeRegisterElement('toggle-classes', ToggleClasses)
safeRegisterElement('autosize-field', AutosizeField)
safeRegisterElement('google-drive-file-picker', GoogleDriveFilePicker)
safeRegisterElement('open-modal', OpenModal)
safeRegisterElement('bar-chart', BarChart)

safeRegisterElement('template-builder', class extends HTMLElement {
  connectedCallback () {
    document.addEventListener('turbo:submit-end', this.onSubmit)

    this.appElem = document.createElement('div')

    this.appElem.classList.add('md:h-screen')

    this.app = createApp(TemplateBuilder, {
      template: reactive(JSON.parse(this.dataset.template)),
      backgroundColor: '#faf7f5',
      locale: this.dataset.locale,
      withPhone: this.dataset.withPhone === 'true',
      withVerification: ['true', 'false'].includes(this.dataset.withVerification) ? this.dataset.withVerification === 'true' : null,
      withLogo: this.dataset.withLogo !== 'false',
      withFieldsDetection: this.dataset.withFieldsDetection === 'true',
      editable: this.dataset.editable !== 'false',
      authenticityToken: document.querySelector('meta[name="csrf-token"]')?.content,
      withPayment: this.dataset.withPayment === 'true',
      isPaymentConnected: this.dataset.isPaymentConnected === 'true',
      withFormula: this.dataset.withFormula === 'true',
      withSendButton: this.dataset.withSendButton !== 'false',
      withSignYourselfButton: this.dataset.withSignYourselfButton !== 'false',
      envelopeId: this.dataset.envelopeId || '',
      envelopeData: this.dataset.envelopeData || null,
      withConditions: this.dataset.withConditions === 'true',
      withGoogleDrive: this.dataset.withGoogleDrive === 'true',
      withReplaceAndCloneUpload: true,
      withDownload: true,
      currencies: (this.dataset.currencies || '').split(',').filter(Boolean),
      acceptFileTypes: this.dataset.acceptFileTypes,
      showTourStartForm: this.dataset.showTourStartForm === 'true'
    })

    this.component = this.app.mount(this.appElem)

    this.appendChild(this.appElem)
  }

  onSubmit = (e) => {
    if (e.detail.success && e.detail?.formSubmission?.formElement?.id === 'submitters_form') {
      e.detail.fetchResponse.response.json().then((data) => {
        this.component.template.submitters = data.submitters
      })
    }
  }

  disconnectedCallback () {
    document.removeEventListener('turbo:submit-end', this.onSubmit)

    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('import-list', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')

    this.app = createApp(ImportList, {
      template: JSON.parse(this.dataset.template),
      multitenant: this.dataset.multitenant === 'true',
      authenticityToken: document.querySelector('meta[name="csrf-token"]')?.content,
      i18n: JSON.parse(this.dataset.i18n || '{}')
    })

    this.app.mount(this.appElem)

    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

import EnvelopeCreator from './template_builder/EnvelopeCreator.jsx'
import TemplateCreator from './template_builder/TemplateCreator.jsx'
import './envelope_share'

// Clear creation tokens when entering the editor
if (window.location.pathname.includes('/templates/') && window.location.pathname.includes('/edit')) {
  sessionStorage.removeItem('template_creation_token')
  sessionStorage.removeItem('envelope_creation_token')
}

// Clear creation tokens when editor loads (fresh navigation, not back button)
window.addEventListener('pageshow', (e) => {
  if (!e.persisted && window.location.pathname.match(/\/templates\/\d+\/edit/)) {
    sessionStorage.removeItem('template_creation_token')
    sessionStorage.removeItem('envelope_creation_token')
  }
})

// Auto-dismiss flash notices after 3 seconds
document.addEventListener('turbo:load', () => {
  const flash = document.getElementById('flash')
  if (flash) {
    setTimeout(() => {
      flash.style.transition = 'opacity 0.5s ease'
      flash.style.opacity = '0'
      setTimeout(() => flash.remove(), 500)
    }, 3000)
  }
})

document.addEventListener('turbo:load', () => {
  const tBtn = document.getElementById('open-template-creator-btn')
  const tMount = document.getElementById('template-creator-mount')
  if (tBtn && tMount) {
    tBtn.addEventListener('click', () => {
      sessionStorage.removeItem('template_creation_token')
      const { createApp, h, ref } = require('vue')
      const app = createApp({
        setup() {
          const show = ref(true)
          return () => show.value ? h(TemplateCreator, {
            onClose: () => { show.value = false; app.unmount() }
          }) : null
        }
      })
      app.mount(tMount)
    })
  }
})
document.addEventListener('turbo:load', () => {
  const btn = document.getElementById('open-envelope-btn')
  const mount = document.getElementById('envelope-mount')
  if (!btn || !mount) return

  btn.addEventListener('click', () => {
    sessionStorage.removeItem('envelope_creation_token')
    const { createApp, h, ref } = require('vue')
    const app = createApp({
      setup() {
        const show = ref(true)
        return () => show.value ? h(EnvelopeCreator, {
          onClose: () => { show.value = false; app.unmount() }
        }) : null
      }
    })
    app.mount(mount)
  })
})

document.addEventListener('turbo:load', () => {
  const storedTemplate = sessionStorage.getItem('template_recipients')
  if (!storedTemplate) return
  try {
    const recipients = JSON.parse(storedTemplate)
    const signers = recipients.filter(r => r.role === 'signer' && r.email)
    if (!signers.length) return
    const emails = signers.map(r => r.email).join(', ')
    new MutationObserver(function(mutations, obs) {
      const ta = document.querySelector('textarea[placeholder="Type emails here..."]')
      if (!ta || ta.dataset.filled) return
      ta.dataset.filled = '1'
      obs.disconnect()
      const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set
      setter.call(ta, emails)
      ta.dispatchEvent(new Event('input', { bubbles: true }))
      sessionStorage.removeItem('template_recipients')
      sessionStorage.removeItem('template_message')
    }).observe(document.body, { childList: true, subtree: true })
  } catch (e) {}
})
document.addEventListener('turbo:load', () => {
  const stored = sessionStorage.getItem('envelope_recipients')
  if (!stored) return

  try {
    const recipients = JSON.parse(stored)
    const signers = recipients.filter(r => r.role === 'signer')
    if (!signers.length) return

    const emails = signers.map(r => r.email).join(', ')

    const observer = new MutationObserver(() => {
      const emailInput = document.querySelector('input[placeholder="Type emails here..."]')
      if (!emailInput) return

      observer.disconnect()

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set
      nativeInputValueSetter.call(emailInput, emails)
      emailInput.dispatchEvent(new Event('input', { bubbles: true }))

      setTimeout(() => {
        const addBtn = Array.from(document.querySelectorAll('button'))
          .find(b => b.textContent.trim().toUpperCase() === 'ADD RECIPIENTS')
        if (addBtn) {
          addBtn.click()
          sessionStorage.removeItem('envelope_recipients')
          sessionStorage.removeItem('envelope_message')
        }
      }, 500)
    })

    observer.observe(document.body, { childList: true, subtree: true })

    setTimeout(() => observer.disconnect(), 10000)

  } catch (e) {
  }
})

document.addEventListener('turbo:load', () => {
  const urlParams = new URLSearchParams(window.location.search)
  if (!urlParams.get('envelope')) return
  const envelopeId = urlParams.get('envelope')

  function injectEnvelopeBtn() {
    if (document.getElementById('envelope-send-btn')) return
    const sendBtn = document.getElementById('send_button')
    if (!sendBtn) return
    sendBtn.style.display = 'none'
    const btn = document.createElement('button')
    btn.id = 'envelope-send-btn'
    btn.textContent = '\u2709 Send Envelope'
    btn.style.cssText = 'background:var(--brand, #1E3A6E);color:#fff;border:none;border-radius:8px;padding:8px 18px;font-weight:700;font-size:13px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:6px'
    btn.addEventListener('click', async () => {
      btn.textContent = 'Sending…'
      btn.disabled = true
      try {
        await new Promise(r => setTimeout(r, 800))
        const csrf = document.querySelector('meta[name=csrf-token]')?.content || ''
        const res = await fetch('/envelopes/send_submission', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          body: JSON.stringify({ envelope_id: envelopeId })
        })
        if (res.ok) {
          const data = await res.json()
          window.location.href = data.redirect_to
        } else {
          throw new Error('Send failed')
        }
      } catch (e) {
        alert('Could not send envelope: ' + e.message)
        btn.textContent = '\u2709 Send Envelope'
        btn.disabled = false
      }
    })
    sendBtn.parentNode.insertBefore(btn, sendBtn)
  }

  const interval = setInterval(() => {
    injectEnvelopeBtn()
    if (document.getElementById('envelope-send-btn')) clearInterval(interval)
  }, 200)
  setTimeout(() => clearInterval(interval), 20000)
})

document.addEventListener('turbo:load', () => {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('open_envelope') === 'true') {
    const btn = document.getElementById('open-envelope-btn')
    if (btn) {
      btn.click()
      const url = new URL(window.location)
      url.searchParams.delete('open_envelope')
      window.history.replaceState({}, '', url)
    }
  }
})

import './permissions'

document.addEventListener('turbo:load', () => {
  document.querySelectorAll('.prof-pw-eye').forEach(function(label) {
    label.addEventListener('click', function() {
      var wrap = label.closest('.prof-pw-wrap');
      if (!wrap) return;
      var input = wrap.querySelector('.prof-pw-field');
      if (!input) return;
      input.type = input.type === 'password' ? 'text' : 'password';
    });
  });
});

// Row overflow menus are popovers, so they render in the top layer and escape .data-card's
// horizontal scroll container instead of being clipped by it. The top layer is outside normal
// flow though, so the menu has to be placed against its trigger by hand. CSS anchor positioning
// would remove this entirely, but it is not in Firefox or Safari yet.
//
// Placed on beforetoggle, while the menu is still hidden, so it never flashes at the UA's
// centred default. Width is known from .row-menu's min-width; height is not measurable until it
// is shown, so the flip-upward check happens on toggle and only near the viewport bottom.
const ROW_MENU_GAP = 6
const ROW_MENU_EDGE = 8

function rowMenuTrigger (menu) {
  return document.querySelector(`[popovertarget="${menu.id}"]`)
}

// Both listeners capture: popover toggle events do not bubble, so a listener on document only
// sees them on the way down. Delegating this way survives Turbo navigations without rebinding.
document.addEventListener('beforetoggle', (event) => {
  const menu = event.target

  if (event.newState !== 'open' || !menu.classList?.contains('row-menu')) return

  const trigger = rowMenuTrigger(menu)

  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const width = parseFloat(getComputedStyle(menu).minWidth) || 190

  // Right-align to the trigger, then keep it inside the viewport.
  menu.style.left = `${Math.max(ROW_MENU_EDGE, Math.min(rect.right - width, window.innerWidth - width - ROW_MENU_EDGE))}px`
  menu.style.top = `${rect.bottom + ROW_MENU_GAP}px`
}, true)

document.addEventListener('toggle', (event) => {
  const menu = event.target

  if (event.newState !== 'open' || !menu.classList?.contains('row-menu')) return

  const trigger = rowMenuTrigger(menu)

  if (!trigger) return

  const rect = trigger.getBoundingClientRect()

  if (menu.getBoundingClientRect().bottom > window.innerHeight - ROW_MENU_EDGE) {
    menu.style.top = `${Math.max(ROW_MENU_EDGE, rect.top - menu.offsetHeight - ROW_MENU_GAP)}px`
  }
}, true)
